import { useState, useEffect } from 'react';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Divider,
	Alert,
} from '@mui/material';

import { authedApiFetch } from 'utils/Api';
import { useTheme } from '@mui/material/styles';
import { useEmailRules } from '../hooks/useEmailRules';
import BasicSettings from './BasicSettings';
import EmailRules from './EmailRules';
import DynamicDomainList from './DynamicDomainList';

let initialModalState = {};
let initialModalStateString = '';

const TenantEmailSyncConfigModal = ({
	tenant,
	open,
	onClose,
	showNotification,
}) => {
	const [inclusionEnabled, setInclusionEnabled] = useState(
		tenant.use_inclusion_email_rules
	);

	const [syncAttachments, setSyncAttachments] = useState(
		tenant.sync_attachments
	);

	const { fetchEmailRules, fetchInitialEmailRules } = useEmailRules(
		true,
		tenant,
		inclusionEnabled,
		open,
		showNotification
	);

	const mapEmailRuleToState = (emailRule) => {
		return {
			ruleType: emailRule.rule_type,
			criteriaType: emailRule.criteria_type,
			value: emailRule.value,
		};
	};

	const stringSort = (a, b) => {
		const nameA = a.toUpperCase();
		const nameB = b.toUpperCase();
		if (nameA < nameB) {
			return -1;
		}
		if (nameA > nameB) {
			return 1;
		}
		return 0;
	};

	const sortSimplifyStringifyModalState = (modalState) => {
		return JSON.stringify({
			includeEmailBody: modalState.includeEmailBody,
			syncAttachments: modalState.syncAttachments,
			emailDirection: modalState.emailDirection,
			inclusionEnabled: modalState.inclusionEnabled,
			domainRules: modalState.domainRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			senderTypeRules: modalState.senderTypeRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			specificEmailRules: modalState.specificEmailRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			salesforceReportId: modalState.salesforceReportId,
		});
	};

	const hasModalStateChanged = () => {
		const newState = {
			includeEmailBody: emailBodySync,
			syncAttachments: syncAttachments,
			emailDirection: emailDirection,
			inclusionEnabled: inclusionEnabled,
			domainRules: domainRules,
			senderTypeRules: senderTypeRules,
			specificEmailRules: specificEmailRules,
			salesforceReportId: (reportId || '').trim(),
		};
		const newStateString = sortSimplifyStringifyModalState(newState);
		return (
			initialModalStateString !== '' &&
			initialModalStateString !== newStateString
		);
	};

	// Controlled form state for the modal
	const [criteriaType, setCriteriaType] = useState('domain');
	const [criteriaValue, setCriteriaValue] = useState('');

	// Email rules
	const [domainRules, setDomainRules] = useState([]);
	const [senderTypeRules, setSenderTypeRules] = useState([]);
	const [specificEmailRules, setSpecificEmailRules] = useState([]);

	const theme = useTheme();

	// 'Include Email Body in CRM Sync' Toggle
	const initialEmailBodySync = tenant?.email_body_sync;

	const [emailBodySync, setEmailBodySync] = useState(initialEmailBodySync);

	// 'Email Ingestion Direction' Dropdown
	const [emailDirection, setEmailDirection] = useState(tenant?.email_direction);

	const handleRuleTypeChange = async (event) => {
		const newInclusionEnabled = event.target.checked;

		setConfigLoading(true);
		setInclusionEnabled(newInclusionEnabled);

		try {
			const newEmailRules = Object.groupBy(
				await fetchInitialEmailRules(newInclusionEnabled),
				(rule) => rule.criteria_type
			);

			setDomainRules(newEmailRules.domain || []);
			setSenderTypeRules(newEmailRules.sender_type || []);
			setSpecificEmailRules(newEmailRules.specific_email || []);
		} finally {
			setConfigLoading(false);
		}
	};

	const handleAddEmailRule = () => {
		const trimmedValue = criteriaValue.trim();

		// Prevent empty or whitespace-only rules from being added
		if (!trimmedValue) {
			return;
		}

		if (criteriaType === 'domain') {
			setDomainRules([
				...domainRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: trimmedValue,
					criteria_type: criteriaType,
					user_id: null,
				},
			]);
		}
		if (criteriaType === 'sender_type') {
			setSenderTypeRules([
				...senderTypeRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: trimmedValue,
					criteria_type: criteriaType,
					user_id: null,
				},
			]);
		}
		if (criteriaType === 'specific_email') {
			setSpecificEmailRules([
				...specificEmailRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: trimmedValue,
					criteria_type: criteriaType,
					user_id: null,
				},
			]);
		}

		setCriteriaValue(''); // Clear form on success
	};

	const handleDeleteEmailRule = (type, id) => {
		if (type === 'domain') {
			setDomainRules(domainRules.filter((rule) => rule.id !== id));
		}
		if (type === 'sender_type') {
			setSenderTypeRules(senderTypeRules.filter((rule) => rule.id !== id));
		}
		if (type === 'specific_email') {
			setSpecificEmailRules(
				specificEmailRules.filter((rule) => rule.id !== id)
			);
		}
	};

	const handleReportId = (updatedReportId) => {
		setReportId(updatedReportId);

		if (updatedReportId) {
			if (criteriaType === 'domain') {
				setCriteriaType('sender_type');
			}
			setDomainRules(initialModalState.domainRules);
		}
	};

	const handleClose = () => {
		const isModalStateInitialized =
			initialModalState &&
			![null, undefined].includes(initialModalState.inclusionEnabled) &&
			![null, undefined].includes(initialModalState.includeEmailBody) &&
			![null, undefined].includes(initialModalState.syncAttachments) &&
			![null, undefined].includes(initialModalState.emailDirection);

		// Discard unsaved changes by restoring the state captured when the modal opened.
		if (isModalStateInitialized && hasModalStateChanged()) {
			setInclusionEnabled(initialModalState.inclusionEnabled);
			setSyncAttachments(initialModalState.syncAttachments);
			setEmailBodySync(initialModalState.includeEmailBody);
			setEmailDirection(initialModalState.emailDirection);

			setDomainRules(initialModalState.domainRules);
			setSenderTypeRules(initialModalState.senderTypeRules);
			setSpecificEmailRules(initialModalState.specificEmailRules);

			setReportId(initialModalState.salesforceReportId);
		}

		// Clear the snapshot so the next modal session starts with fresh values
		initialModalStateString = '';
		initialModalState = {};

		onClose();
	};

	const handleSave = async () => {
		const trimmedReportId = (reportId || '').trim();

		if (
			trimmedReportId &&
			emailSyncConfig &&
			emailSyncConfig.report_id &&
			emailSyncConfig.report_id !== trimmedReportId
		) {
			const okToUpdate = window.confirm(
				`Update Report ID from "${emailSyncConfig.report_id}" to "${trimmedReportId}"?\nThis will reset the current sync state.`
			);
			if (!okToUpdate) return;
		}

		if (
			!trimmedReportId &&
			emailSyncConfig &&
			emailSyncConfig.report_id &&
			emailSyncConfig.status === 'running'
		) {
			showNotification(
				'Cannot delete while sync is running. Please wait for it to finish.',
				'warning'
			);
			return;
		}
		if (
			!trimmedReportId &&
			emailSyncConfig &&
			emailSyncConfig.report_id &&
			emailSyncConfig.status !== 'running'
		) {
			const okToDelete = window.confirm(
				'Delete Dynamic Domain List configuration? This will disable the feature.'
			);
			if (!okToDelete) return;
		}

		const newState = {
			include_email_body: emailBodySync,
			email_direction: emailDirection,
			inclusion_enabled: inclusionEnabled,
			sync_attachments: syncAttachments,
			domain_rules: domainRules,
			sender_type_rules: senderTypeRules,
			specific_email_rules: specificEmailRules,
			report_id: trimmedReportId,
		};

		// Only persist the configuration when the current modal values differ
		// from the state captured when the modal was opened
		if (hasModalStateChanged()) {
			const message = {
				successMessage: `Successfully updated!`,
				errorMessage: `Failed to update.`,
				errorLog:
					'Failed to update email sync config settings due to the error:',
			};
			try {
				await authedApiFetch({
					endpoint: `/tenants/${tenant.id}/email_sync_configuration_settings`,
					method: 'POST',
					payload: {
						tenant_level: true,
						new_state: newState,
					},
				});

				tenant.use_inclusion_email_rules = inclusionEnabled;

				tenant.email_body_sync = emailBodySync;
				tenant.email_direction = emailDirection;
				tenant.sync_attachments = syncAttachments;

				setInclusionEnabled(inclusionEnabled);
				setSyncAttachments(syncAttachments);
				setEmailBodySync(emailBodySync);
				setEmailDirection(emailDirection);

				showNotification(message.successMessage, 'success');
			} catch (error) {
				handleClose();
				console.error(message.errorLog, error.message);
				showNotification(message.errorMessage, 'error');
				return;
			}
		}

		// Clear the captured initial state before closing so the next modal
		// session initializes its change tracking from freshly loaded values
		initialModalStateString = '';
		initialModalState = {};
		onClose();
	};

	// Dynamic Domain List (Report-driven sync)
	const [emailSyncConfig, setEmailSyncConfig] = useState(null);
	const [reportId, setReportId] = useState('');
	const [configLoading, setConfigLoading] = useState(false);
	const [runningEmailDomainSync, setRunningEmailDomainSync] = useState(false);
	const isRunning = emailSyncConfig?.status === 'running';
	const hasConfig = !!emailSyncConfig;

	const fetchEmailDomainSyncConfig = async () => {
		try {
			const cfg = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/email_domain_sync`,
				method: 'GET',
			});

			setEmailSyncConfig(cfg);
			setReportId(cfg.report_id || '');

			return cfg;
		} catch (error) {
			setEmailSyncConfig(null);
			setReportId('');
			return null;
		}
	};

	// Fetch latest status from backend; returns updated config or null if 404
	const fetchEmailDomainSyncStatus = async () => {
		try {
			const cfg = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/email_domain_sync`,
				method: 'GET',
			});
			setEmailSyncConfig(cfg);
			return cfg;
		} catch (error) {
			// authedApiFetch currently wraps HTTP errors without preserving `status`,
			// so check the error message as a fallback until status codes are exposed
			const isNotFound =
				error?.status === 404 ||
				error?.message?.includes('unsuccessful response: 404');

			if (isNotFound) {
				setEmailSyncConfig(null);
			}

			return null;
		}
	};

	useEffect(() => {
		if (!open || !tenant?.id) return;

		const fetchData = async () => {
			// keep the modal in a loading state until all initial configuration
			// and email rule data has finished loading
			setConfigLoading(true);

			try {
				const fetchedEmailDomainSyncConfig = await fetchEmailDomainSyncConfig();

				const fetchedReportId = fetchedEmailDomainSyncConfig?.report_id || '';

				const initialEmailRules = Object.groupBy(
					await fetchInitialEmailRules(inclusionEnabled),
					(r) => r.criteria_type
				);

				const initialDomainRules = initialEmailRules.domain || [];
				const initialSenderRules = initialEmailRules.sender_type || [];
				const initialSpecificEmailRules =
					initialEmailRules.specific_email || [];

				// use tenant values directly to avoid stale state when detecting unsaved changes
				const shouldIncludeEmailBody = tenant?.email_body_sync;
				const initialEmailDirection = tenant?.email_direction;
				const initialSyncAttachments = tenant?.sync_attachments;

				setDomainRules(initialDomainRules);
				setSenderTypeRules(initialSenderRules);
				setSpecificEmailRules(initialSpecificEmailRules);

				setEmailBodySync(shouldIncludeEmailBody);
				setEmailDirection(initialEmailDirection);
				setSyncAttachments(initialSyncAttachments);

				setRunningEmailDomainSync(
					fetchedEmailDomainSyncConfig?.status === 'running'
				);

				// Store initial values to detect and reset unsaved changes
				initialModalState = {
					includeEmailBody: shouldIncludeEmailBody,
					syncAttachments: initialSyncAttachments,
					emailDirection: initialEmailDirection,
					inclusionEnabled: tenant.use_inclusion_email_rules,
					domainRules: initialDomainRules,
					senderTypeRules: initialSenderRules,
					specificEmailRules: initialSpecificEmailRules,
					salesforceReportId: fetchedReportId.trim(),
				};

				initialModalStateString =
					sortSimplifyStringifyModalState(initialModalState);
			} finally {
				// Keep loading active until async initialization completes
				setConfigLoading(false);
			}
		};

		fetchData();
	}, [open, tenant?.id]);

	// Poll while the Dynamic Domain List is syncing
	useEffect(() => {
		if (!open || !tenant?.id || emailSyncConfig?.status !== 'running') {
			return;
		}

		const interval = setInterval(async () => {
			const config = await fetchEmailDomainSyncStatus();

			if (config?.status === 'running') {
				setRunningEmailDomainSync(true);
				return;
			}

			clearInterval(interval);
			setRunningEmailDomainSync(false);

			// Refresh domain rules after the dynamic domain sync completes
			if (config?.status === 'succeeded') {
				fetchEmailRules();
			}
		}, 2000);

		return () => clearInterval(interval);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, tenant?.id, emailSyncConfig?.status]);

	// If a config exists, do not allow selecting 'domain' for manual rule creation
	useEffect(() => {
		if (hasConfig && criteriaType === 'domain') {
			setCriteriaType('sender_type');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasConfig]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='md'
			fullWidth
			scroll='paper'
		>
			<DialogTitle>Email Sync Configuration</DialogTitle>

			<DialogContent dividers>
				{hasModalStateChanged() && (
					<Alert
						severity='info'
						sx={{
							mb: 2,
							backgroundColor: theme.palette.background.lightBlue,
						}}
					>
						There are unsaved changes to your Email Sync Configuration, to save
						them, please press the Save button.
					</Alert>
				)}

				<BasicSettings
					tenant={tenant}
					tenantLevel={true}
					emailBodySync={emailBodySync}
					setEmailBodySync={setEmailBodySync}
					syncAttachments={syncAttachments}
					setSyncAttachments={setSyncAttachments}
					emailDirection={emailDirection}
					setEmailDirection={setEmailDirection}
				/>

				<Divider sx={{ my: 3 }} />

				<EmailRules
					tenantLevel={true}
					inclusionEnabled={inclusionEnabled}
					handleRuleTypeChange={handleRuleTypeChange}
					isRunning={isRunning}
					criteriaType={criteriaType}
					setCriteriaType={setCriteriaType}
					criteriaValue={criteriaValue}
					setCriteriaValue={setCriteriaValue}
					handleAddEmailRule={handleAddEmailRule}
					handleDeleteEmailRule={handleDeleteEmailRule}
					domainRules={domainRules}
					senderTypeRules={senderTypeRules}
					specificEmailRules={specificEmailRules}
					hasConfig={hasConfig}
					reportId={reportId}
				/>

				<Divider sx={{ mt: 4, mb: 3 }} />

				<DynamicDomainList
					tenantLevel={true}
					reportId={reportId}
					handleReportId={handleReportId}
					configLoading={configLoading}
					runningEmailDomainSync={runningEmailDomainSync}
					emailSyncConfig={emailSyncConfig}
				/>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} variant='contained'>
					Cancel
				</Button>

				<Button onClick={handleSave} variant='contained'>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TenantEmailSyncConfigModal;
